import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { apiGet } from "../../../../api/apiGet";
import {
  getCustomerDetailsForOrder,
  setCustomerPo,
  setShippingAddress,
} from "../../../../store/slices/CustomerSlice/CustomerSlice";
import { deepObjectEqual } from "../../../../helpers/helperFunctions";
import InfoIcon from "../../../atoms/InfoIcon/InfoIcon";
import { resetShippingInfo } from "../../../../store/slices/OrderSlice/OrderSlice";

export default function CustomerInformation({ setParentError }) {
  const {
    customer: { name, customer_num, shipping_address, billing_address },
    canChangeShippingAddress,
    isDropShip,
    originalShippingAddress,
    customerPo,
  } = useSelector((state) => state.customer);

  const dispatch = useDispatch();

  const [commonInputProps, setCommonInputProps] = useState({
    disabled: !canChangeShippingAddress,
    className: `mt-1 block w-full rounded-md px-3 py-1.5 text-sm shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-[var(--primary-color)]
    bg-white text-gray-900 ring-gray-300
    disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed disabled:ring-gray-200`,
  });

  // Initialize shipping address - use existing shipping_address if it has content, otherwise empty
  const [shippingAddressLocal, setShippingAddressLocal] = useState(() => {
    // Check if shipping_address exists and has meaningful content
    const hasExistingContent =
      shipping_address &&
      (shipping_address.name ||
        shipping_address.street ||
        shipping_address.street_two ||
        shipping_address.city ||
        shipping_address.state ||
        shipping_address.zip_code ||
        shipping_address.contact_email ||
        shipping_address.contact_fax ||
        shipping_address.contact_name ||
        shipping_address.contact_phone);

    if (hasExistingContent) {
      return {
        name: shipping_address.name || "",
        street: shipping_address.street || "",
        street_two: shipping_address.street_two || "",
        city: shipping_address.city || "",
        state: shipping_address.state || "",
        zip_code: shipping_address.zip_code || "",
        contact_email: shipping_address.contact_email || "",
        contact_fax: shipping_address.contact_fax || "",
        contact_name: shipping_address.contact_name || "",
        contact_phone: shipping_address.contact_phone || "",
      };
    }

    // Default to empty if no existing content
    return {
      name: "",
      street: "",
      street_two: "",
      city: "",
      state: "",
      zip_code: "",
      contact_email: "",
      contact_fax: "",
      contact_name: "",
      contact_phone: "",
    };
  });

  const [newPo, setNewPo] = useState(customerPo || "");

  const [duplicatePoOrders, setDuplicatePoOrders] = useState([]);
  const [poCheckLoading, setPoCheckLoading] = useState(false);
  const poDebounceRef = useRef(null);

  const [errors, setErrors] = useState({
    street: false,
    city: false,
    state: false,
    zip_code: false,
  });

  // Update disabled state based on canChangeShippingAddress
  useEffect(() => {
    setCommonInputProps((prev) => ({
      ...prev,
      disabled: !canChangeShippingAddress,
    }));
  }, [canChangeShippingAddress]);

  // Only fetch customer details if not already loaded, but don't auto-populate shipping address
  useEffect(() => {
    if (!shipping_address || !billing_address) {
      dispatch(getCustomerDetailsForOrder());
    }
  }, [shipping_address, billing_address, dispatch]);

  const zipDebounceRef = useRef(null);
  useEffect(() => {
    const zip = shippingAddressLocal?.zip_code ?? "";
    if (!/^\d{5}$/.test(zip.split("-")[0])) return;
    clearTimeout(zipDebounceRef.current);
    zipDebounceRef.current = setTimeout(async () => {
      try {
        const res = await apiGet.getZipLookup(zip.split("-")[0]);
        const { city, state } = res.data;
        setShippingAddressLocal((prev) => ({
          ...prev,
          city: prev.city || city,
          state: prev.state || state,
        }));
      } catch {
        // ZIP not found — leave city/state as-is
      }
    }, 500);
    return () => clearTimeout(zipDebounceRef.current);
  }, [shippingAddressLocal?.zip_code]);

  // Debounced duplicate PO check — only for new orders (ID = 0)
  useEffect(() => {
    if (!newPo || !customer_num) {
      setDuplicatePoOrders([]);
      return;
    }

    clearTimeout(poDebounceRef.current);
    setPoCheckLoading(true);

    poDebounceRef.current = setTimeout(async () => {
      try {
        const res = await apiGet.checkDuplicatePo({
          customer_num,
          customer_po: newPo,
        });
        const results = res?.data?.results ?? [];
        // Filter out self-match (current order ID = 0 means new, nothing to filter)
        setDuplicatePoOrders(results);
      } catch {
        setDuplicatePoOrders([]);
      } finally {
        setPoCheckLoading(false);
      }
    }, 1500);

    return () => {
      clearTimeout(poDebounceRef.current);
      setPoCheckLoading(false);
    };
  }, [newPo, customer_num]);

  // Function to populate shipping address with customer's original shipping address
  const useCustomerAddress = () => {
    if (
      originalShippingAddress &&
      Object.keys(originalShippingAddress).length > 0
    ) {
      setShippingAddressLocal({
        name:
          originalShippingAddress.name ||
          originalShippingAddress.contact_name ||
          "",
        street: originalShippingAddress.street || "",
        street_two: originalShippingAddress.street_two || "",
        city: originalShippingAddress.city || "",
        state: originalShippingAddress.state || "",
        zip_code: originalShippingAddress.zip_code || "",
        contact_email: originalShippingAddress.contact_email || "",
        contact_fax: originalShippingAddress.contact_fax || "",
        contact_name: originalShippingAddress.contact_name || "",
        contact_phone: originalShippingAddress.contact_phone || "",
      });
    } else {
      console.log(
        "No original shipping address available or original shipping address is empty",
      );
    }
  };

  // Validate address and update parent error state
  useEffect(() => {
    if (shippingAddressLocal) {
      const newErrors = {
        street: !(shippingAddressLocal.street?.length >= 5),
        city: !(shippingAddressLocal.city?.length >= 3),
        state: !(shippingAddressLocal.state?.length === 2),
        zip_code: !/^\d{5}(-\d{4})?$/.test(shippingAddressLocal.zip_code),
      };

      setErrors(newErrors);
      setParentError(Object.values(newErrors).some(Boolean));
    }
  }, [shippingAddressLocal, setParentError]);

  // Update Redux store with local shipping address (only if not empty)
  useEffect(() => {
    // Only dispatch if the local state has meaningful data (not all empty fields)
    const hasData =
      shippingAddressLocal &&
      (shippingAddressLocal.street ||
        shippingAddressLocal.city ||
        shippingAddressLocal.state ||
        shippingAddressLocal.zip_code);

    if (hasData) {
      // Debounce the dispatch to avoid too many Redux updates
      const timeoutId = setTimeout(() => {
        const addressChanged = !deepObjectEqual(
          originalShippingAddress,
          shippingAddressLocal,
        );

        dispatch(
          setShippingAddress({
            shippingAddress: shippingAddressLocal,
            isDropShip: addressChanged,
          }),
        );

        if (addressChanged) {
          dispatch(resetShippingInfo());
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [shippingAddressLocal, dispatch, originalShippingAddress]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setShippingAddressLocal((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePoChange = (e) => {
    const truncated = e.target.value.slice(0, 25);
    setNewPo(truncated);
    dispatch(setCustomerPo(truncated));
  };

  const clearShippingAddress = () => {
    setShippingAddressLocal({
      name: "",
      street: "",
      street_two: "",
      city: "",
      state: "",
      zip_code: "",
      contact_email: "",
      contact_fax: "",
      contact_name: "",
      contact_phone: "",
    });
  };

  return (
    <form>
      <div className="space-y-12 sm:space-y-16 px-6 pt-6">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900">
            Verify Customer Information
          </h2>
          <p className="mt-1 max-w-2xl text-sm/6 text-gray-600">
            This information is will be used for shipping and billing, if you
            see something that is incorrect that you are unable to change, reach
            out to your sales representative.
          </p>

          <div className="mt-10 space-y-4 border-b border-gray-900/10 sm:space-y-0 sm:border-t sm:pb-0">
            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:pt-6 pb-2">
              <div className="flex text-sm/6 font-medium text-gray-900 sm:pt-1.5">
                Customer{" "}
                <InfoIcon info="If you need to change this information, please reach out to a sales representative before proceeding." />
              </div>
              <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="flex items-center rounded-md bg-white pl-3 outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:max-w-md">
                  <div className="shrink-0 text-base text-gray-500 select-none sm:text-sm/6">
                    {`${customer_num || ""} - ${name || ""}`}
                  </div>
                </div>
              </div>
            </div>
            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:pb-6">
              <div className="flex gap-2 text-sm/6 font-medium text-gray-900 sm:pt-1.5">
                Billing Address{" "}
                <InfoIcon info="If you need to change this information, please reach out to a sales representative before proceeding." />
              </div>
              <div className="sm:col-span-2 sm:mt-0">
                <div className="flex-col items-center rounded-md bg-white pl-3 outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:max-w-md">
                  <div className="shrink-0 text-base text-gray-500 select-none sm:text-sm/6">
                    {`${billing_address?.street} ${billing_address?.street_two} ${billing_address?.city}, ${billing_address?.state} ${billing_address?.zip_code}`}
                  </div>
                  <div className="shrink-0 text-base text-gray-500 select-none sm:text-sm/6">
                    {`${billing_address?.contact_name}, E:${billing_address?.contact_email}`}
                  </div>
                  <div className="shrink-0 text-base text-gray-500 select-none sm:text-sm/6">
                    {`F:${billing_address?.contact_fax}, P:${billing_address?.contact_phone}`}
                  </div>
                </div>
              </div>
            </div>
            <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:pb-6">
              <div className="flex gap-2 text-sm/6 font-medium text-gray-900 sm:pt-1.5">
                Customer Order PO{" "}
              </div>
              <div className="sm:col-span-2 sm:mt-0">
                <div className="flex-col items-center rounded-md bg-white pl-3 outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:max-w-md">
                  <input
                    id="contact"
                    name="name"
                    value={newPo}
                    type="text"
                    maxLength={25}
                    autoComplete="name"
                    onChange={handlePoChange}
                    {...commonInputProps}
                  />
                  <p
                    className={`text-xs mt-1 ${
                      newPo.length >= 25 ? "text-red-500" : "text-gray-400"
                    }`}
                  >
                    {newPo.length}/25
                  </p>
                  {poCheckLoading && (
                    <p className="text-xs mt-1 text-gray-400 italic">
                      Checking for duplicate PO…
                    </p>
                  )}
                  {!poCheckLoading && duplicatePoOrders.length > 0 && (
                    <div className="mt-2 rounded-md bg-yellow-50 border border-yellow-300 p-3">
                      <div className="flex items-start gap-2">
                        <svg
                          className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <div>
                          <p className="text-xs font-semibold text-yellow-800">
                            Duplicate PO Warning
                          </p>
                          <p className="text-xs text-yellow-700 mt-0.5">
                            This PO number has already been used in the following order{duplicatePoOrders.length > 1 ? "s" : ""}:
                          </p>
                          <ul className="mt-1 space-y-0.5">
                            {duplicatePoOrders.map((order) => (
                              <li
                                key={order.customer_order_num}
                                className="text-xs text-yellow-700"
                              >
                                Order #{order.customer_order_num}
                                {order.date_of_order
                                  ? ` — ${new Date(order.date_of_order).toLocaleDateString()}`
                                  : ""}
                                {order.customer_invoice_num
                                  ? ` (Invoice #${order.customer_invoice_num})`
                                  : ""}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="relative border-b border-gray-900/10 pb-12 grid items-start grid-cols-3 mt-6">
            {/* Section label */}
            <div className="text-sm/6 font-medium text-gray-900 sm:pt-1.5">
              <div className="flex items-center gap-1">
                Shipping Information
                <InfoIcon info="If you are unable to change this information, please reach out to an administrator if you believe this is an error." />
              </div>
              {isDropShip && (
                <p className="text-xs text-gray-500 font-normal mt-1">
                  **Address Changed
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-2 mt-3 max-w-full sm:max-w-md">
                <button
                  type="button"
                  onClick={useCustomerAddress}
                  disabled={!canChangeShippingAddress}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Use Customer Address
                </button>
                <button
                  type="button"
                  onClick={clearShippingAddress}
                  disabled={!canChangeShippingAddress}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-2 border border-red-300 shadow-sm text-xs font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Clear Address
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 lg:grid-cols-8 col-span-2">
              {/* Name */}
              <div className="lg:col-span-2">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-900"
                >
                  Name
                </label>
                <input
                  id="contact"
                  name="name"
                  value={shippingAddressLocal?.name ?? ""}
                  type="text"
                  autoComplete="name"
                  maxLength={30}
                  onChange={handleChange}
                  {...commonInputProps}
                />
              </div>

              {/* Street */}
              <div className="lg:col-span-3">
                <label
                  htmlFor="street"
                  className="block text-sm font-medium text-gray-900"
                >
                  Street
                </label>
                <input
                  {...commonInputProps}
                  id="street-address1"
                  name="street"
                  value={shippingAddressLocal?.street ?? ""}
                  type="text"
                  maxLength={40}
                  autoComplete="street-address"
                  onChange={handleChange}
                  className={
                    commonInputProps.className +
                    ` mt-1 block w-full rounded-md shadow-sm sm:text-sm border ${
                      errors?.street &&
                      "border-red-500 focus:border-red-500 focus:ring-red-500"
                    }`
                  }
                />
                {errors?.street && <RequireSubtext />}
              </div>

              {/* Street pt. 2 */}
              <div className="lg:col-span-3">
                <label
                  htmlFor="street_two"
                  className="block text-sm font-medium text-gray-900"
                >
                  Street pt. 2
                </label>
                <input
                  id="street-address2"
                  name="street_two"
                  value={shippingAddressLocal?.street_two ?? ""}
                  type="text"
                  maxLength={40}
                  autoComplete="street-address"
                  onChange={handleChange}
                  {...commonInputProps}
                />
              </div>

              {/* City */}
              <div className="lg:col-span-2">
                <label
                  htmlFor="city"
                  className="block text-sm font-medium text-gray-900"
                >
                  City
                </label>
                <input
                  {...commonInputProps}
                  id="city"
                  name="city"
                  value={shippingAddressLocal?.city ?? ""}
                  type="text"
                  maxLength={20}
                  autoComplete="address-level2"
                  onChange={handleChange}
                  className={
                    commonInputProps.className +
                    ` mt-1 block w-full rounded-md shadow-sm sm:text-sm border ${
                      errors?.city &&
                      "border-red-500 focus:border-red-500 focus:ring-red-500"
                    }`
                  }
                />
                {errors?.city && <RequireSubtext />}
              </div>

              {/* State */}
              <div className="lg:col-span-3">
                <label
                  htmlFor="state"
                  className="block text-sm font-medium text-gray-900"
                >
                  State
                </label>
                <input
                  {...commonInputProps}
                  id="region"
                  name="state"
                  value={shippingAddressLocal?.state ?? ""}
                  type="text"
                  autoComplete="address-level1"
                  maxLength={2}
                  onChange={handleChange}
                  className={
                    commonInputProps.className +
                    ` mt-1 block w-full rounded-md shadow-sm sm:text-sm border ${
                      errors?.state &&
                      "border-red-500 focus:border-red-500 focus:ring-red-500"
                    }`
                  }
                />
                {errors?.state && <RequireSubtext addOn={"Ex: GA"} />}
              </div>

              {/* Zip Code */}
              <div className="lg:col-span-3">
                <label
                  htmlFor="zip_code"
                  className="block text-sm font-medium text-gray-900"
                >
                  Zip Code
                </label>
                <input
                  {...commonInputProps}
                  id="postal-code"
                  name="zip_code"
                  value={shippingAddressLocal?.zip_code ?? ""}
                  type="text"
                  onChange={handleChange}
                  autoComplete="postal-code"
                  maxLength={10}
                  className={
                    commonInputProps.className +
                    ` mt-1 block w-full rounded-md shadow-sm sm:text-sm border ${
                      errors?.zip_code &&
                      "border-red-500 focus:border-red-500 focus:ring-red-500"
                    }`
                  }
                />
                {errors?.zip_code && <RequireSubtext addOn={"Ex: 30001"} />}
              </div>

              {/* Email */}
              <div className="lg:col-span-2">
                <label
                  htmlFor="contact_email"
                  className="block text-sm font-medium text-gray-900"
                >
                  Email
                </label>
                <input
                  id="email"
                  name="contact_email"
                  value={shippingAddressLocal?.contact_email ?? ""}
                  type="email"
                  maxLength={60}
                  autoComplete="email"
                  onChange={handleChange}
                  {...commonInputProps}
                />
              </div>

              {/* Fax */}
              <div className="lg:col-span-2">
                <label
                  htmlFor="contact_fax"
                  className="block text-sm font-medium text-gray-900"
                >
                  Fax
                </label>
                <input
                  id="fax"
                  name="contact_fax"
                  value={shippingAddressLocal?.contact_fax ?? ""}
                  type="text"
                  maxLength={14}
                  onChange={handleChange}
                  {...commonInputProps}
                />
              </div>

              {/* Contact Name */}
              <div className="lg:col-span-2">
                <label
                  htmlFor="contact_name"
                  className="block text-sm font-medium text-gray-900"
                >
                  Contact Name
                </label>
                <input
                  id="contact-name"
                  name="contact_name"
                  value={shippingAddressLocal?.contact_name ?? ""}
                  type="text"
                  maxLength={20}
                  autoComplete="name"
                  onChange={handleChange}
                  {...commonInputProps}
                />
              </div>

              {/* Phone */}
              <div className="lg:col-span-2">
                <label
                  htmlFor="contact_phone"
                  className="block text-sm font-medium text-gray-900"
                >
                  Phone
                </label>
                <input
                  id="phone"
                  name="contact_phone"
                  value={shippingAddressLocal?.contact_phone ?? ""}
                  type="tel"
                  maxLength={14}
                  autoComplete="tel"
                  onChange={handleChange}
                  {...commonInputProps}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

function RequireSubtext({ addOn }) {
  return (
    <p className="mt-1 text-sm text-red-600">
      **This Field is Required {addOn ? ` - ${addOn}` : ""}
    </p>
  );
}
